import type { ServiceQueryOptions } from '@/lib/http/queryUtils';
import { chainService } from '@/lib/services/chain.service';
import { notificationService } from '@/lib/services/notification.service';
import { permissionsService } from '@/lib/services/permissions.service';
import { subscriptionService } from '@/lib/services/subscription.service';
import { getCurrentTenantSubdomain } from '@/utils/app-domain';
import type { QueryClient } from '@tanstack/react-query';

export type CompanyBootstrapQueries = {
	/** The workspace the session boots into, from the `DEFAULT_DATA` cookie. */
	companyId: string;
};

/**
 * Every company-scoped query the authenticated shell mounts on boot: permissions gate the rail and
 * topbar, chain info and its counts pick the dashboard variant, plan usage feeds the topbar pill,
 * and the unread count feeds the bell.
 *
 * Each entry comes from its own service's query-options builder, so the key and request function are
 * literally the ones the hook uses and the hook lands on this cache entry instead of refetching.
 */
const companyBootstrapQueries = ({ companyId }: CompanyBootstrapQueries): Array<ServiceQueryOptions<unknown>> => [
	permissionsService.useMyEffectivePermissions.queryOptions({}),
	chainService.useGetChainInfo.queryOptions({}),
	chainService.useGetAggregatedCounts.queryOptions({}),
	subscriptionService.useGetUsage.queryOptions({}),
	notificationService.useGetUnreadCount.queryOptions({ query: { companyId } }),
];

/**
 * An authenticated visit to the tenant-less `app.<domain>` entry point always hard-redirects to a
 * workspace subdomain (`AppWithVadioDomain`), discarding this tab's query cache with it - so any
 * prefetch fired there is pure waste. Tenant subdomains, custom domains, and the e2e host (which
 * has no subdomain concept) all keep the session in this tab and do benefit.
 */
const isDiscardedOnEntryDomain = (): boolean => {
	if (import.meta.env.VITE_APP_ENV === 'e2e') return false;
	if (typeof window === 'undefined') return true;

	const onAppDomain = window.location.hostname.includes(import.meta.env.VITE_APP_DOMAIN);
	return onAppDomain && getCurrentTenantSubdomain() === null;
};

export type CompanyBootstrapPrefetch = CompanyBootstrapQueries & {
	queryClient: QueryClient;
};

/**
 * Starts the company-scoped bootstrap queries in the same burst as `/profile/me` + `/companies`,
 * using the workspace the cookie already names at t=0 - without this they only start once hydrate
 * has resolved and the shell has mounted, a full extra request layer into the boot.
 *
 * Fire-and-forget on purpose: hydrate must never wait on these. A cookie naming a workspace the user
 * can no longer reach just fails into an empty cache entry (`prefetchQuery` swallows errors), and
 * hydrate's own reconciliation stays the authority on which workspace is actually selected.
 */
export const prefetchCompanyBootstrapQueries = ({ queryClient, companyId }: CompanyBootstrapPrefetch): void => {
	if (!companyId) return;
	if (isDiscardedOnEntryDomain()) return;

	for (const options of companyBootstrapQueries({ companyId })) {
		void queryClient.prefetchQuery(options);
	}
};

export const dropCompanyBootstrapQueries = ({ queryClient, companyId }: CompanyBootstrapPrefetch): void => {
	if (!companyId) return;

	for (const { queryKey } of companyBootstrapQueries({ companyId })) {
		void queryClient.cancelQueries({ queryKey, exact: true });
		queryClient.removeQueries({ queryKey, exact: true });
	}
};
